import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Logo } from './Logo';

/**
 * Shared public-site top navigation. Appears on every customer-facing
 * page so the brand + primary nav stays consistent. Excluded from the
 * wizard pages (Search, Confirm, AuthCode, Pay, Success) which use
 * `WizardLayout` for a focused checkout-style experience, and from the
 * admin dashboard which uses `AdminLayout`.
 *
 * Anchor links on this header (How it works, Pricing, FAQ) point at
 * sections of the landing page. When the user is already on the landing
 * page these resolve as same-page hash jumps; otherwise we render them
 * as full `/#section` links so the browser navigates home first then
 * scrolls.
 *
 * On mobile (< md breakpoint) the inline nav collapses into a hamburger
 * menu that slides down. Otherwise mobile users wouldn't be able to
 * reach Services / Blog / Contact from the header — they'd have to
 * scroll all the way to the footer.
 */
export function SiteHeader() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const isHome = pathname === '/';

  // Close the mobile menu when the route changes.
  useEffect(() => setOpen(false), [pathname]);

  // Build hash-style anchor link relative to the landing page so it
  // works whether we're on / or any other route.
  const hash = (id: string) => (isHome ? `#${id}` : `/#${id}`);

  // Close the menu after any nav link tap. Needed because hash links on the
  // landing page don't change pathname, so the route-change effect doesn't
  // fire and the menu would otherwise stay open.
  const close = () => setOpen(false);

  const navLinks = (variant: 'desktop' | 'mobile') => (
    <>
      <Link to="/services" onClick={close} className={linkClass(pathname.startsWith('/services'), variant)}>
        Services
      </Link>
      <a href={hash('how')} onClick={close} className={linkClass(false, variant)}>How it works</a>
      <a href={hash('pricing')} onClick={close} className={linkClass(false, variant)}>Pricing</a>
      <a href={hash('faq')} onClick={close} className={linkClass(false, variant)}>FAQ</a>
      <Link to="/blog" onClick={close} className={linkClass(pathname.startsWith('/blog'), variant)}>Blog</Link>
      <Link to="/contact" onClick={close} className={linkClass(pathname.startsWith('/contact'), variant)}>
        Contact
      </Link>
    </>
  );

  return (
    <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/85 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-6 py-3">
        <div className="flex items-center gap-2">
          {/* Mobile hamburger — only shown below md, placed left of logo */}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            className="flex h-9 w-9 items-center justify-center rounded-md text-slate-700 hover:bg-slate-100 md:hidden"
          >
            {open ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            )}
          </button>

          <Link to="/" aria-label="Dormant Accounts Filing – home">
            <Logo />
          </Link>
        </div>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-6 text-sm text-slate-600 md:flex">
          {navLinks('desktop')}
        </nav>

        <Link to="/search" className="btn-primary whitespace-nowrap">File now</Link>
      </div>

      {/* Mobile menu — drops down under the header. */}
      {open && (
        <nav className="border-t border-slate-200 bg-white md:hidden">
          <div className="mx-auto flex max-w-6xl flex-col gap-1 px-6 py-3 text-sm">
            {navLinks('mobile')}
          </div>
        </nav>
      )}
    </header>
  );
}

function linkClass(active: boolean, variant: 'desktop' | 'mobile'): string {
  if (variant === 'mobile') {
    return `rounded-md px-3 py-2 ${
      active ? 'bg-brand-50 font-semibold text-brand-700' : 'text-slate-700 hover:bg-slate-50'
    }`;
  }
  return active ? 'font-semibold text-brand-700' : 'hover:text-slate-900';
}
